import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Alert,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  getRoles,
  getUsers,
  getRoleModules,
} from "../../../../utils/services/control/masterDataService";

const PermissionCell = ({ allowed }) =>
  allowed ? (
    <CheckIcon sx={{ fontSize: 16, color: "success.main" }} />
  ) : (
    <CloseIcon sx={{ fontSize: 16, color: "text.disabled" }} />
  );

const RolePermissionsView = () => {
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [rolesData, usersData] = await Promise.all([
          getRoles(),
          getUsers(),
        ]);
        setRoles(rolesData);
        setUsers(usersData);
      } catch (err) {
        console.error("Error loading roles:", err);
      }
    };
    load();
  }, []);

  const handleRoleSelect = async (e) => {
    const roleId = e.target.value;
    setSelectedRoleId(roleId);
    setError("");

    if (!roleId) {
      setModules([]);
      return;
    }

    // Necesitamos un employeeId que tenga este rol para consultar getRoleModules
    // Buscamos cualquier usuario activo
    const anyUser = users[0];
    if (!anyUser) {
      setError("No users available to query role modules. Create a user first.");
      return;
    }

    setLoading(true);
    try {
      const data = await getRoleModules(anyUser.employeeId, Number(roleId));
      if (Array.isArray(data)) {
        setModules(data);
      } else if (data.result) {
        setError(data.message || "Error loading modules");
        setModules([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error loading role modules");
      setModules([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Permissions by Role (Read-Only)
      </Typography>

      <FormControl size="small" sx={{ minWidth: 250, mb: 2 }}>
        <InputLabel>Select Role</InputLabel>
        <Select
          value={selectedRoleId}
          label="Select Role"
          onChange={handleRoleSelect}
        >
          <MenuItem value="">—</MenuItem>
          {roles.map((r) => (
            <MenuItem key={r.id} value={r.id}>
              {r.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <CircularProgress size={24} />
      ) : modules.length > 0 ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Module</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Create
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Read
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Update
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Delete
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Access
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {modules.map((mod) => (
                <TableRow key={mod.moduleId}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {mod.moduleName}
                      <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                        {mod.moduleKey}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <PermissionCell allowed={mod.canCreate} />
                  </TableCell>
                  <TableCell align="center">
                    <PermissionCell allowed={mod.canRead} />
                  </TableCell>
                  <TableCell align="center">
                    <PermissionCell allowed={mod.canUpdate} />
                  </TableCell>
                  <TableCell align="center">
                    <PermissionCell allowed={mod.canDelete} />
                  </TableCell>
                  <TableCell align="center">
                    {mod.hasAccess ? (
                      <Chip label="Yes" size="small" color="success" />
                    ) : (
                      <Chip label="No" size="small" variant="outlined" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        selectedRoleId && (
          <Typography variant="body2" color="text.secondary">
            No module permissions found for this role.
          </Typography>
        )
      )}
    </Box>
  );
};

export default RolePermissionsView;
